import React, { useState } from "react";
import useAppContext from "Contexts/app-context";
import { useTranslation, useMobileMode } from "Shared/util/custom-hooks";
import {
    cancelSeBankIdAuthentication,
    authenticateEIdSeBankId,
    authenticateEIdFiTupas,
} from "Shared/util/web-api";
import {
    EIdAuthenticationFiTupasResource,
    EIdAuthenticationPurpose,
    EIdAuthenticationSeBankIdResource,
    EIdProvider,
} from "Shared/util/types";
import { useSeBankIdPoller } from "./hooks/se-bankid-poller-hook";
import { useEIdCallbackHandler } from "./hooks/eid-callback-handler-hook";
import FadeTransition from "Shared/components/transitions/fade-transition";
import Button from "Shared/components/button";
import Spinner from "Shared/components/spinner";
import TextLink from "Shared/components/text-link";
import {
    getUserAgentResource,
    userAgentBrowsers,
    userAgentDevices,
} from "../user-agent";
import styles from "./eid-authentication-hook.css";

enum BankIdMethod {
    NONE,
    OTHER_DEVICE,
    SAME_DEVICE,
}

type SeBankId = {
    applicationUrl?: string;
    qrCodeImage?: string;
    selectedMethod: BankIdMethod;
    token?: string;
};

type UseEIdAuthenticationResult = {
    renderEIdAuthentication: (
        description?: string,
        className?: string,
    ) => JSX.Element;
    resetEIdAuthentication: () => void;
    isAuthenticatingEId: boolean;
};

/** Note: The invoking component must be rendered on page load for callback handler to be triggered. */
export const useEIdAuthentication = (
    registerPromise: (p: Promise<any>) => Promise<any>,
    authenticationPurpose: EIdAuthenticationPurpose,
    onAuthenticationSucceeded: (token: string) => void,
    onAuthenticationFailed?: () => void,
    onAuthenticationError?: () => void,
    onAuthenticationStarted?: () => void,
    disabled?: boolean,
): UseEIdAuthenticationResult => {
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [seBankIdData, setSeBankIdData] = useState<SeBankId>({
        selectedMethod: BankIdMethod.NONE,
    });
    const {
        appData: { eIdProvider },
    } = useAppContext();
    const isMobileMode = useMobileMode();
    const t = useTranslation((x) => x.eId);
    const tg = useTranslation((x) => x.global);

    const resetAuthentication = () => {
        setIsAuthenticating(false);
        if (eIdProvider === EIdProvider.SeBankId) {
            setSeBankIdData({ selectedMethod: BankIdMethod.NONE });
        }
    };

    const abortAuthentication = () => {
        if (eIdProvider === EIdProvider.SeBankId) {
            seBankIdPoller.stop();

            if (seBankIdData.token) {
                registerPromise(
                    cancelSeBankIdAuthentication(seBankIdData.token),
                );
            }
        }
    };

    const resetAndAbortAuthentication = () => {
        abortAuthentication();
        resetAuthentication();
    };

    const handleAuthenticationSucceeded = (token: string) => {
        resetAuthentication();
        onAuthenticationSucceeded(token);
    };

    const handleAuthenticationFailed = () => {
        resetAndAbortAuthentication();
        onAuthenticationFailed && onAuthenticationFailed();
    };

    const handleAuthenticationError = () => {
        resetAndAbortAuthentication();
        onAuthenticationError && onAuthenticationError();
    };

    const handleCallback = () => {
        setIsAuthenticating(true);
        if (eIdProvider === EIdProvider.SeBankId) {
            setSeBankIdData({ selectedMethod: BankIdMethod.SAME_DEVICE });
        }
    };

    useEIdCallbackHandler(
        registerPromise,
        eIdProvider,
        authenticationPurpose,
        handleCallback,
        handleAuthenticationSucceeded,
        handleAuthenticationFailed,
        handleAuthenticationError,
    );
    const seBankIdPoller = useSeBankIdPoller(
        registerPromise,
        handleAuthenticationSucceeded,
        handleAuthenticationFailed,
        handleAuthenticationError,
    );

    const handleAuthenticationStarted = () => {
        setIsAuthenticating(true);
        onAuthenticationStarted && onAuthenticationStarted();
    };

    const renderSeBankIdView = () => {
        if (seBankIdData.selectedMethod === BankIdMethod.NONE) {
            const beginSeBankIdAuthentication = (
                bankIdMethod: BankIdMethod,
            ) => {
                handleAuthenticationStarted();

                const userAgent = getUserAgentResource();

                registerPromise(
                    authenticateEIdSeBankId(authenticationPurpose, userAgent),
                )
                    .then((response: EIdAuthenticationSeBankIdResource) => {
                        const {
                            applicationUrl,
                            callbackUrl,
                            qrCodeImage,
                            token,
                        } = response;

                        setSeBankIdData({
                            applicationUrl: applicationUrl,
                            qrCodeImage: qrCodeImage,
                            selectedMethod: bankIdMethod,
                            token: token,
                        });

                        if (
                            bankIdMethod === BankIdMethod.SAME_DEVICE &&
                            applicationUrl
                        ) {
                            if (
                                userAgentDevices.isIos() &&
                                userAgentBrowsers.isSafari() &&
                                callbackUrl
                            ) {
                                window.history.replaceState(
                                    null,
                                    "",
                                    callbackUrl,
                                );
                            }
                            window.location.href = applicationUrl;
                        }

                        seBankIdPoller.start(token, (updatedQrCode) => {
                            setSeBankIdData((x) => ({
                                ...x,
                                qrCodeImage: updatedQrCode,
                            }));
                        });
                    })
                    .catch(() => {
                        resetAndAbortAuthentication();
                        onAuthenticationError && onAuthenticationError();
                    });
            };

            const handleBankIdOtherDevice = () => {
                setSeBankIdData({ selectedMethod: BankIdMethod.OTHER_DEVICE });
                beginSeBankIdAuthentication(BankIdMethod.OTHER_DEVICE);
            };

            const handleBankIdSameDevice = () => {
                setSeBankIdData({ selectedMethod: BankIdMethod.SAME_DEVICE });
                beginSeBankIdAuthentication(BankIdMethod.SAME_DEVICE);
            };

            const buttons = [
                <Button
                    key="otherDevice"
                    buttonType={
                        isMobileMode ? "action-secondary" : "action-primary"
                    }
                    text={t.seBankID.bankid_on_another_device}
                    onClick={handleBankIdOtherDevice}
                    attributes={{ disabled: isAuthenticating || !!disabled }}
                    className={styles.bankIdButton}
                />,
                <Button
                    key="sameDevice"
                    buttonType={
                        isMobileMode ? "action-primary" : "action-secondary"
                    }
                    text={t.seBankID.open_bankid}
                    onClick={handleBankIdSameDevice}
                    attributes={{ disabled: isAuthenticating || !!disabled }}
                    className={styles.bankIdButton}
                />,
            ];

            return isMobileMode ? buttons.reverse() : buttons;
        } else if (seBankIdData.selectedMethod === BankIdMethod.OTHER_DEVICE) {
            return (
                <>
                    <p className={styles.bankIdDescription}>
                        {t.seBankID.qr_code_instructions}
                    </p>
                    <FadeTransition
                        isOpen={!!seBankIdData.qrCodeImage}
                        enterDuration={100}
                        className={styles.bankIdQrCodeImageContainer}
                    >
                        {!seBankIdData.qrCodeImage ? (
                            <></>
                        ) : (
                            <img
                                src={seBankIdData.qrCodeImage}
                                className={styles.bankIdQrCodeImage}
                                alt="QR code"
                            />
                        )}
                    </FadeTransition>
                    {!seBankIdData.qrCodeImage && (
                        <div className={styles.bankIdQrCodeImageContainer}>
                            <img
                                src={"/eid/qr-code-skeleton.png"}
                                className={styles.bankIdQrCodeImage}
                                alt="QR Code"
                            />
                        </div>
                    )}
                    <TextLink
                        text={tg.cancel}
                        link={resetAndAbortAuthentication}
                        className={styles.cancelBankIdTextLink}
                    />
                </>
            );
        } else if (seBankIdData.selectedMethod === BankIdMethod.SAME_DEVICE) {
            return (
                <>
                    <p className={styles.bankIdDescription}>
                        {t.seBankID.waiting_for_identification}
                    </p>
                    <Spinner />
                    <TextLink
                        text={tg.cancel}
                        link={resetAndAbortAuthentication}
                        className={styles.cancelBankIdTextLink}
                    />
                </>
            );
        } else {
            return null;
        }
    };

    const renderFiTupasView = () => {
        const beginFiTupasAuthentication = () => {
            handleAuthenticationStarted();

            registerPromise(authenticateEIdFiTupas(authenticationPurpose))
                .then((response: EIdAuthenticationFiTupasResource) => {
                    window.location.href = response.redirectUrl;
                })
                .catch(() => {
                    resetAndAbortAuthentication();
                    onAuthenticationError && onAuthenticationError();
                });
        };

        return (
            <>
                <p className={styles.fiTupasDescription}>
                    {t.fiTupas.instructions}
                </p>
                <Button
                    buttonType="action-primary"
                    text={t.fiTupas.identify}
                    onClick={beginFiTupasAuthentication}
                    isLoading={isAuthenticating}
                    attributes={{ disabled: !!disabled }}
                    className={styles.fiTupasButton}
                />
            </>
        );
    };

    const render = (className?: string) => {
        let view;
        switch (eIdProvider) {
            case EIdProvider.SeBankId:
                view = renderSeBankIdView();
                break;
            case EIdProvider.FiTupas:
                view = renderFiTupasView();
                break;
            default:
                throw new Error(`Invalid eIdProvider: ${eIdProvider}`);
        }

        return <div className={className ? className : ""}>{view}</div>;
    };

    return {
        renderEIdAuthentication: render,
        resetEIdAuthentication: resetAndAbortAuthentication,
        isAuthenticatingEId: isAuthenticating,
    };
};
